import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getContractor, updateContractorDocument } from "../services/contractorsService";
import { validateDocumentFile, getDocumentAcceptValue, getDocumentHelpText } from "../utils/documentValidation";
import styles from "./ContractorDocumentCreate.module.css";

const ContractorDocumentEdit = () => {
  const { id, documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [document, setDocument] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    document: null,
  });
  const [formErrors, setFormErrors] = useState({});

  // Load contractor and document data
  useEffect(() => {
    loadData();
  }, [id, documentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getContractor(id);
      if (response.success) {
        setContractor(response.data);
        // Find the document to edit
        const doc = response.data.documents?.find(d => d.id == documentId);
        if (doc) {
          setDocument(doc);
          setFormData(prev => ({
            ...prev,
            name: doc.name || ""
          }));
        } else {
          throw new Error("Document not found");
        }
      } else {
        throw new Error("Failed to load contractor");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Error loading data");
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData(prev => ({
        ...prev,
        document: null
      }));
      return;
    }

    // Validate file
    const validationResult = validateDocumentFile(file);
    if (!validationResult.isValid) {
      toast.error(validationResult.error);
      e.target.value = "";
      return;
    }

    setFormData(prev => ({
      ...prev,
      document: file
    }));

    // Clear error when user selects a file
    if (formErrors.document) {
      setFormErrors(prev => ({
        ...prev,
        document: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name is optional for editing
    // Document is optional for editing (only if user wants to replace)

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("contractor_id", contractor.id);
      submitData.append("name", formData.name);
      
      // Only append document if a new one is selected
      if (formData.document) {
        submitData.append("document", formData.document);
      }

      const response = await updateContractorDocument(id, documentId, submitData);
      
      if (response.success) {
        toast.success("Document updated successfully!");
        // Update local document state with response data to show updated name immediately
        if (response.data && response.data.document) {
          setDocument(prev => ({
            ...prev,
            ...response.data.document
          }));
        }
        // Navigate after a brief delay to show updated data
        setTimeout(() => {
          navigate(`/contractors/${id}`);
        }, 300);
      } else {
        throw new Error(response.message || "Failed to update document");
      }
    } catch (error) {
      console.error("Error updating document:", error);
      toast.error(error.message || "Error updating document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/contractors/${id}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleCancel} className={styles.btnSecondary}>
            Back to Contractor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumbs}>
        <span>Contractors</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>{contractor?.name}</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>Edit Document</span>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Edit contractor document</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.fieldset}>
                  <input 
                    type="hidden" 
                    name="contractor_id" 
                    value={contractor?.id} 
                  />

                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.controlLabel}>
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="You can leave it blank"
                      />
                      {formErrors.name && (
                        <span className={styles.errorText}>{formErrors.name}</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="document" className={styles.controlLabel}>
                      Document (Optional - only if you want to replace the current file)
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.inputImage}>
                        <input
                          type="file"
                          id="document"
                          name="document"
                          onChange={handleFileChange}
                          className={styles.formControl}
                          accept={getDocumentAcceptValue()}
                        />
                        {formErrors.document && (
                          <span className={styles.errorText}>{formErrors.document}</span>
                        )}
                        <small className={styles.helpText}>
                          Current file: {document?.file || document?.name || 'No file'}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>
                </fieldset>

                <div className={styles.formGroup}>
                  <div className={styles.textRight}>
                    <button
                      type="submit"
                      className={styles.btnHead}
                      disabled={submitting}
                    >
                      {submitting ? "Updating document..." : "Save document"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorDocumentEdit;
